import type {
  AcquiredDisposed,
  AmendmentState,
  CanonicalTransaction,
  DashboardCoverage,
  DashboardDownloadCounters,
  DashboardExportCounters,
  DashboardManifest,
  DashboardManifestCounters,
  DashboardParseCounters,
  DashboardValidationCounters,
  DatasetMode,
  FreshnessState,
  IngestState,
  InsiderRole,
  PlanEvidence,
  MarketCapBucket,
  MarketContextSource,
  ProgramContext,
  SecurityStatus,
  Signal,
  SignalBucket,
  SignalConfidence,
  SupersessionState,
  TradeType,
  TransactionCluster,
  TransactionMarketContext,
  TransactionReturnWindow,
  ValidationIssue,
  ValidityState,
  ValueQuality
} from "./types";

export const DASHBOARD_SCHEMA_VERSION = 2 as const;
export const DASHBOARD_SIGNAL_VERSION = "insider-signal-v2" as const;

export type DashboardContractErrorCode =
  | "invalid_type"
  | "invalid_literal"
  | "invalid_number"
  | "invalid_count"
  | "invalid_date"
  | "invalid_timestamp"
  | "invalid_url"
  | "invalid_score"
  | "invalid_health"
  | "security_invariant"
  | "alias_mismatch"
  | "counter_mismatch"
  | "missing_shards"
  | "invalid_shard_name"
  | "transaction_count_mismatch"
  | "shard_count_mismatch"
  | "empty_transaction_id"
  | "duplicate_transaction_id"
  | "id_alias_mismatch"
  | "signal_version_mismatch"
  | "cluster_member_missing";

export class DashboardContractError extends Error {
  readonly code: DashboardContractErrorCode;
  readonly path: string;

  constructor(
    code: DashboardContractErrorCode,
    path: string,
    message = `${code} at ${path}`
  ) {
    super(message);
    this.name = "DashboardContractError";
    this.code = code;
    this.path = path;
  }
}

const SHARD_NAME_PATTERN = /^insider-dashboard-transactions-[0-9a-f]{12}\.json$/;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T.+(?:Z|[+-]\d{2}:\d{2})$/;

function fail(code: DashboardContractErrorCode, path: string, message?: string): never {
  throw new DashboardContractError(code, path, message);
}

function record(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    fail("invalid_type", path, `Expected object at ${path}`);
  }
  return value as Record<string, unknown>;
}

function stringValue(value: unknown, path: string, allowEmpty = true): string {
  if (typeof value !== "string" || (!allowEmpty && value.trim() === "")) {
    fail("invalid_type", path, `Expected ${allowEmpty ? "string" : "non-empty string"} at ${path}`);
  }
  return value;
}

function nullableString(value: unknown, path: string): string | null {
  return value === null ? null : stringValue(value, path);
}

function secArchiveUrlValue(value: unknown, path: string): string {
  const raw = stringValue(value, path, false);
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    fail("invalid_url", path, `Expected canonical SEC archive URL at ${path}`);
  }
  if (
    parsed.protocol !== "https:" ||
    parsed.hostname !== "www.sec.gov" ||
    parsed.port !== "" ||
    parsed.username !== "" ||
    parsed.password !== "" ||
    parsed.search !== "" ||
    parsed.hash !== "" ||
    !/^\/Archives\/edgar\/data\/\d+\/[^/]+(?:\/[^/]+)*$/.test(parsed.pathname) ||
    parsed.href !== raw
  ) {
    fail("invalid_url", path, `Expected canonical SEC archive URL at ${path}`);
  }
  return raw;
}

function booleanValue(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") fail("invalid_type", path, `Expected boolean at ${path}`);
  return value;
}

function literalValue<const T extends readonly string[]>(
  value: unknown,
  allowed: T,
  path: string
): T[number] {
  if (typeof value !== "string" || !allowed.includes(value)) {
    fail("invalid_literal", path, `Unsupported value at ${path}`);
  }
  return value as T[number];
}

function finiteNumber(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    fail("invalid_number", path, `Expected finite number at ${path}`);
  }
  return value;
}

function nullableNumber(value: unknown, path: string): number | null {
  return value === null ? null : finiteNumber(value, path);
}

function countValue(value: unknown, path: string): number {
  if (typeof value !== "number") {
    fail("invalid_type", path, `Expected number at ${path}`);
  }
  if (!Number.isInteger(value) || value < 0) {
    fail("invalid_count", path, `Expected nonnegative integer at ${path}`);
  }
  return value;
}

function nullableInteger(value: unknown, path: string): number | null {
  if (value === null) return null;
  if (typeof value !== "number" || !Number.isInteger(value)) {
    fail("invalid_number", path, `Expected integer or null at ${path}`);
  }
  return value;
}

function arrayValue<T>(
  value: unknown,
  path: string,
  parseItem: (item: unknown, itemPath: string) => T
): T[] {
  if (!Array.isArray(value)) fail("invalid_type", path, `Expected array at ${path}`);
  return value.map((item, index) => parseItem(item, `${path}[${index}]`));
}

function stringArray(value: unknown, path: string, allowEmptyItems = true): string[] {
  return arrayValue(value, path, (item, itemPath) => stringValue(item, itemPath, allowEmptyItems));
}

function dateValue(value: unknown, path: string): string {
  const date = stringValue(value, path, false);
  const match = DATE_PATTERN.exec(date);
  if (!match) fail("invalid_date", path, `Expected ISO date at ${path}`);
  const parsed = new Date(`${date}T00:00:00.000Z`);
  if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date) {
    fail("invalid_date", path, `Expected real ISO date at ${path}`);
  }
  return date;
}

function dateOrEmpty(value: unknown, path: string): string {
  const date = stringValue(value, path);
  return date === "" ? date : dateValue(date, path);
}

function nullableDate(value: unknown, path: string): string | null {
  return value === null ? null : dateValue(value, path);
}

function timestampValue(value: unknown, path: string): string {
  const timestamp = stringValue(value, path, false);
  if (!TIMESTAMP_PATTERN.test(timestamp) || !Number.isFinite(Date.parse(timestamp))) {
    fail("invalid_timestamp", path, `Expected offset-aware ISO timestamp at ${path}`);
  }
  return timestamp;
}

function timestampOrEmpty(value: unknown, path: string): string {
  const timestamp = stringValue(value, path);
  return timestamp === "" ? timestamp : timestampValue(timestamp, path);
}

function parseValidationIssue(value: unknown, path: string): ValidationIssue {
  const input = record(value, path);
  return {
    code: stringValue(input.code, `${path}.code`, false),
    severity: literalValue(input.severity, ["warning", "critical"] as const, `${path}.severity`),
    message: stringValue(input.message, `${path}.message`, false)
  };
}

function parseDownloadCounters(value: unknown, path: string): DashboardDownloadCounters {
  const input = record(value, path);
  return {
    indexRowsSeen: countValue(input.indexRowsSeen, `${path}.indexRowsSeen`),
    inUniverseRowsSeen: countValue(input.inUniverseRowsSeen, `${path}.inUniverseRowsSeen`),
    downloadedFilings: countValue(input.downloadedFilings, `${path}.downloadedFilings`),
    alreadyPresentFilings: countValue(
      input.alreadyPresentFilings,
      `${path}.alreadyPresentFilings`
    ),
    errors: countValue(input.errors, `${path}.errors`)
  };
}

function parseParseCounters(value: unknown, path: string): DashboardParseCounters {
  const input = record(value, path);
  return {
    errors: countValue(input.errors, `${path}.errors`),
    zeroTransactionFilings: countValue(
      input.zeroTransactionFilings,
      `${path}.zeroTransactionFilings`
    )
  };
}

function parseExportCounters(value: unknown, path: string): DashboardExportCounters {
  const input = record(value, path);
  return {
    transactions: countValue(input.transactions, `${path}.transactions`),
    shards: countValue(input.shards, `${path}.shards`)
  };
}

function parseValidationCounters(value: unknown, path: string): DashboardValidationCounters {
  const input = record(value, path);
  return {
    criticalErrors: countValue(input.criticalErrors, `${path}.criticalErrors`),
    warnings: countValue(input.warnings, `${path}.warnings`)
  };
}

function parseManifestCounters(value: unknown, path: string): DashboardManifestCounters {
  const input = record(value, path);
  return {
    download: parseDownloadCounters(input.download, `${path}.download`),
    parse: parseParseCounters(input.parse, `${path}.parse`),
    export: parseExportCounters(input.export, `${path}.export`),
    validation: parseValidationCounters(input.validation, `${path}.validation`)
  };
}

function parseFreshnessCompatibility(value: unknown, path: string): FreshnessState {
  const input = record(value, path);
  return {
    latestRefresh: timestampValue(input.latestRefresh, `${path}.latestRefresh`),
    latestFilingProcessed: dateOrEmpty(
      input.latestFilingProcessed,
      `${path}.latestFilingProcessed`
    ),
    status: literalValue(input.status, ["fresh", "stale", "error"] as const, `${path}.status`),
    processedFilings: countValue(input.processedFilings, `${path}.processedFilings`),
    message: stringValue(input.message, `${path}.message`)
  };
}

function parseDashboardCoverage(value: unknown, path: string): DashboardCoverage {
  const input = record(value, path);
  const exchangeCoverage = arrayValue(input.exchangeCoverage, `${path}.exchangeCoverage`, (item, itemPath) => {
    const exchange = record(item, itemPath);
    return {
      exchange: stringValue(exchange.exchange, `${itemPath}.exchange`, false),
      tickerCount: countValue(exchange.tickerCount, `${itemPath}.tickerCount`),
      transactionCount: countValue(exchange.transactionCount, `${itemPath}.transactionCount`)
    };
  });
  return {
    mode: stringValue(input.mode, `${path}.mode`, false),
    filingWindowStart: dateOrEmpty(input.filingWindowStart, `${path}.filingWindowStart`),
    filingWindowEnd: dateOrEmpty(input.filingWindowEnd, `${path}.filingWindowEnd`),
    tradeWindowStart: dateOrEmpty(input.tradeWindowStart, `${path}.tradeWindowStart`),
    tradeWindowEnd: dateOrEmpty(input.tradeWindowEnd, `${path}.tradeWindowEnd`),
    tickerCount: countValue(input.tickerCount, `${path}.tickerCount`),
    cikCount: countValue(input.cikCount, `${path}.cikCount`),
    latestUniverseRefresh: timestampOrEmpty(
      input.latestUniverseRefresh,
      `${path}.latestUniverseRefresh`
    ),
    latestIngestStartedAt: timestampOrEmpty(
      input.latestIngestStartedAt,
      `${path}.latestIngestStartedAt`
    ),
    latestIngestFinishedAt: timestampOrEmpty(
      input.latestIngestFinishedAt,
      `${path}.latestIngestFinishedAt`
    ),
    indexRowsSeen: countValue(input.indexRowsSeen, `${path}.indexRowsSeen`),
    inUniverseRowsSeen: countValue(input.inUniverseRowsSeen, `${path}.inUniverseRowsSeen`),
    downloadedFilings: countValue(input.downloadedFilings, `${path}.downloadedFilings`),
    alreadyPresentFilings: countValue(
      input.alreadyPresentFilings,
      `${path}.alreadyPresentFilings`
    ),
    duplicateIndexRows: countValue(input.duplicateIndexRows, `${path}.duplicateIndexRows`),
    outOfUniverseRows: countValue(input.outOfUniverseRows, `${path}.outOfUniverseRows`),
    downloadErrors: countValue(input.downloadErrors, `${path}.downloadErrors`),
    parseErrors: countValue(input.parseErrors, `${path}.parseErrors`),
    zeroTransactionFilings: countValue(
      input.zeroTransactionFilings,
      `${path}.zeroTransactionFilings`
    ),
    archiveFilingWindowStart: dateOrEmpty(
      input.archiveFilingWindowStart ?? input.filingWindowStart,
      `${path}.archiveFilingWindowStart`
    ),
    archiveFilingWindowEnd: dateOrEmpty(
      input.archiveFilingWindowEnd ?? input.filingWindowEnd,
      `${path}.archiveFilingWindowEnd`
    ),
    archiveTransactionCount: countValue(
      input.archiveTransactionCount ?? 0,
      `${path}.archiveTransactionCount`
    ),
    screenerFilingWindowStart: dateOrEmpty(
      input.screenerFilingWindowStart ?? input.filingWindowStart,
      `${path}.screenerFilingWindowStart`
    ),
    screenerFilingWindowEnd: dateOrEmpty(
      input.screenerFilingWindowEnd ?? input.filingWindowEnd,
      `${path}.screenerFilingWindowEnd`
    ),
    screenerTransactionCount: countValue(
      input.screenerTransactionCount ?? 0,
      `${path}.screenerTransactionCount`
    ),
    exportMode: literalValue(
      input.exportMode ?? "full",
      ["full", "windowed"] as const,
      `${path}.exportMode`
    ) as "full" | "windowed",
    exchangeCoverage,
    note: stringValue(input.note, `${path}.note`)
  };
}

function validateIssueCounts(
  issues: ValidationIssue[],
  criticalErrorCount: number,
  warningCount: number,
  validationCounters: DashboardValidationCounters,
  nestedValidationCounters: DashboardValidationCounters
): void {
  const structuredCritical = issues.filter((issue) => issue.severity === "critical").length;
  const structuredWarnings = issues.filter((issue) => issue.severity === "warning").length;
  if (criticalErrorCount !== structuredCritical) fail("counter_mismatch", "criticalErrorCount");
  if (warningCount !== structuredWarnings) fail("counter_mismatch", "warningCount");
  if (validationCounters.criticalErrors !== structuredCritical) {
    fail("counter_mismatch", "validationCounters.criticalErrors");
  }
  if (validationCounters.warnings !== structuredWarnings) {
    fail("counter_mismatch", "validationCounters.warnings");
  }
  if (nestedValidationCounters.criticalErrors !== structuredCritical) {
    fail("counter_mismatch", "counters.validation.criticalErrors");
  }
  if (nestedValidationCounters.warnings !== structuredWarnings) {
    fail("counter_mismatch", "counters.validation.warnings");
  }
}

function validateHealthCombination(
  ingestState: IngestState,
  snapshotFromLastCompletedRun: boolean,
  validityState: ValidityState,
  criticalErrorCount: number,
  warningCount: number
): void {
  if (ingestState === "completed" && snapshotFromLastCompletedRun) {
    fail("invalid_health", "snapshotFromLastCompletedRun");
  }
  if (ingestState !== "completed") {
    const requiredValidity = snapshotFromLastCompletedRun ? "degraded" : "invalid";
    if (validityState !== requiredValidity) fail("invalid_health", "validityState");
  }

  const issueDerivedValidity =
    criticalErrorCount > 0 ? "invalid" : warningCount > 0 ? "degraded" : "valid";
  if (validityState !== issueDerivedValidity) fail("invalid_health", "validityState");
}

export function parseDashboardManifest(value: unknown): DashboardManifest {
  const input = record(value, "manifest");
  const schemaVersion = input.schemaVersion;
  if (schemaVersion !== DASHBOARD_SCHEMA_VERSION) {
    fail("invalid_literal", "schemaVersion", "Unsupported dashboard schema version");
  }
  const signalVersion = input.signalVersion;
  if (signalVersion !== DASHBOARD_SIGNAL_VERSION) {
    fail("invalid_literal", "signalVersion", "Unsupported dashboard signal version");
  }

  const generatedAt = timestampValue(input.generatedAt, "generatedAt");
  const staleAfter = timestampValue(input.staleAfter, "staleAfter");
  const latestFilingProcessed = dateOrEmpty(input.latestFilingProcessed, "latestFilingProcessed");
  const ingestState = literalValue(
    input.ingestState,
    ["completed", "incomplete", "failed"] as const,
    "ingestState"
  ) as IngestState;
  const snapshotFromLastCompletedRun = booleanValue(
    input.snapshotFromLastCompletedRun,
    "snapshotFromLastCompletedRun"
  );
  const datasetMode = literalValue(
    input.datasetMode,
    ["live", "demo", "partial"] as const,
    "datasetMode"
  ) as DatasetMode;
  const validityState = literalValue(
    input.validityState,
    ["valid", "degraded", "invalid"] as const,
    "validityState"
  ) as ValidityState;
  const criticalErrorCount = countValue(input.criticalErrorCount, "criticalErrorCount");
  const warningCount = countValue(input.warningCount, "warningCount");
  const validationIssues = arrayValue(
    input.validationIssues,
    "validationIssues",
    parseValidationIssue
  );
  const downloadCounters = parseDownloadCounters(input.downloadCounters, "downloadCounters");
  const parseCounters = parseParseCounters(input.parseCounters, "parseCounters");
  const exportCounters = parseExportCounters(input.exportCounters, "exportCounters");
  const validationCounters = parseValidationCounters(
    input.validationCounters,
    "validationCounters"
  );
  const counters = parseManifestCounters(input.counters, "counters");
  const freshness = parseFreshnessCompatibility(input.freshness, "freshness");
  const coverage = parseDashboardCoverage(input.coverage, "coverage");
  const transactions = arrayValue(input.transactions, "transactions", parseCanonicalTransaction);
  const transactionCount = countValue(input.transactionCount, "transactionCount");
  const transactionShardUrls = stringArray(
    input.transactionShardUrls,
    "transactionShardUrls",
    false
  );

  validateIssueCounts(
    validationIssues,
    criticalErrorCount,
    warningCount,
    validationCounters,
    counters.validation
  );
  validateHealthCombination(
    ingestState,
    snapshotFromLastCompletedRun,
    validityState,
    criticalErrorCount,
    warningCount
  );

  if (datasetMode === "live" && transactionCount === 0 && validityState !== "invalid") {
    fail("invalid_health", "transactionCount", "An empty live dataset must be invalid");
  }
  if (transactionCount > 0 && transactionShardUrls.length === 0) {
    fail("missing_shards", "transactionShardUrls");
  }
  transactionShardUrls.forEach((url, index) => {
    if (!SHARD_NAME_PATTERN.test(url)) {
      fail("invalid_shard_name", `transactionShardUrls[${index}]`);
    }
  });

  return {
    schemaVersion,
    signalVersion,
    generatedAt,
    staleAfter,
    latestFilingProcessed,
    ingestState,
    snapshotFromLastCompletedRun,
    datasetMode,
    validityState,
    criticalErrorCount,
    warningCount,
    validationIssues,
    downloadCounters,
    parseCounters,
    exportCounters,
    validationCounters,
    counters,
    freshness,
    coverage,
    transactions,
    transactionCount,
    transactionShardUrls
  };
}

function parsePlan(value: unknown, path: string): PlanEvidence {
  const input = record(value, path);
  return {
    status: literalValue(
      input.status,
      ["planned", "not_planned", "unknown"] as const,
      `${path}.status`
    ),
    evidenceSource: literalValue(
      input.evidenceSource,
      ["structured", "footnote", "both", "none"] as const,
      `${path}.evidenceSource`
    ),
    evidenceExcerpts: stringArray(input.evidenceExcerpts, `${path}.evidenceExcerpts`)
  };
}

function parseCluster(value: unknown, path: string): TransactionCluster | null {
  if (value === null) return null;
  const input = record(value, path);
  const distinctInsiderCount = countValue(
    input.distinctInsiderCount,
    `${path}.distinctInsiderCount`
  );
  if (distinctInsiderCount < 2) {
    fail("invalid_count", `${path}.distinctInsiderCount`);
  }
  const memberTransactionIds = stringArray(
    input.memberTransactionIds,
    `${path}.memberTransactionIds`,
    false
  );
  if (memberTransactionIds.length < 2) fail("invalid_count", `${path}.memberTransactionIds`);
  return {
    clusterId: stringValue(input.clusterId, `${path}.clusterId`, false),
    version: literalValue(input.version, ["cluster-14d-v1"] as const, `${path}.version`),
    windowStart: dateValue(input.windowStart, `${path}.windowStart`),
    windowEnd: dateValue(input.windowEnd, `${path}.windowEnd`),
    memberTransactionIds,
    distinctInsiderCount
  };
}

function parseProgramContext(value: unknown, path: string): ProgramContext | null {
  if (value === null) return null;
  const input = record(value, path);
  return {
    sameTickerDatePriceCluster: booleanValue(
      input.sameTickerDatePriceCluster,
      `${path}.sameTickerDatePriceCluster`
    ),
    groupSize: countValue(input.groupSize, `${path}.groupSize`),
    distinctOwnerCount: countValue(input.distinctOwnerCount, `${path}.distinctOwnerCount`)
  };
}

function parseReturnWindow(value: unknown, path: string): TransactionReturnWindow | null {
  if (value === null) return null;
  const input = record(value, path);
  return {
    d1: nullableNumber(input.d1, `${path}.d1`),
    d5: nullableNumber(input.d5, `${path}.d5`),
    d20: nullableNumber(input.d20, `${path}.d20`)
  };
}

function parseMarketContext(value: unknown, path: string): TransactionMarketContext {
  const input = record(value, path);
  return {
    marketCap: nullableNumber(input.marketCap, `${path}.marketCap`),
    marketCapBucket: literalValue(
      input.marketCapBucket,
      ["micro", "small", "mid", "large", "mega", "unknown"] as const,
      `${path}.marketCapBucket`
    ) as MarketCapBucket,
    valueToMarketCapBps: nullableNumber(input.valueToMarketCapBps, `${path}.valueToMarketCapBps`),
    ownershipMaterialityPct: nullableNumber(
      input.ownershipMaterialityPct,
      `${path}.ownershipMaterialityPct`
    ),
    priceAtTrade: nullableNumber(input.priceAtTrade, `${path}.priceAtTrade`),
    priceAtFiling: nullableNumber(input.priceAtFiling, `${path}.priceAtFiling`),
    returnsFromTrade: parseReturnWindow(input.returnsFromTrade, `${path}.returnsFromTrade`),
    returnsFromFiling: parseReturnWindow(input.returnsFromFiling, `${path}.returnsFromFiling`),
    source: literalValue(
      input.source,
      ["missing", "derived", "enriched"] as const,
      `${path}.source`
    ) as MarketContextSource
  };
}

function parseSignal(value: unknown, path: string): Signal {
  const input = record(value, path);
  const rawScore = input.score;
  let score: number | null;
  if (rawScore === null) {
    score = null;
  } else if (
    typeof rawScore !== "number" ||
    !Number.isFinite(rawScore) ||
    rawScore < 0 ||
    rawScore > 100
  ) {
    fail("invalid_score", `${path}.score`);
  } else {
    score = rawScore;
  }
  return {
    bucket: literalValue(
      input.bucket,
      [
        "informative_buy",
        "informative_sell",
        "uninformative_buy",
        "uninformative_sell",
        "needs_review"
      ] as const,
      `${path}.bucket`
    ) as SignalBucket,
    score,
    confidence: literalValue(
      input.confidence,
      ["high", "medium", "low"] as const,
      `${path}.confidence`
    ) as SignalConfidence,
    reasons: stringArray(input.reasons, `${path}.reasons`),
    suppressions: stringArray(input.suppressions, `${path}.suppressions`),
    version: literalValue(
      input.version,
      [DASHBOARD_SIGNAL_VERSION] as const,
      `${path}.version`
    )
  };
}

export function parseCanonicalTransaction(value: unknown): CanonicalTransaction {
  const input = record(value, "transaction");
  const transactionId = stringValue(input.transactionId, "transactionId", false);
  const id = stringValue(input.id, "id", false);
  const industryDescription = stringValue(
    input.industryDescription,
    "industryDescription",
    false
  );
  const sector = stringValue(input.sector, "sector", false);
  const issuerCik = stringValue(input.issuerCik, "issuerCik", false);
  const cik = stringValue(input.cik, "cik", false);
  if (id !== transactionId) fail("alias_mismatch", "id");
  if (sector !== industryDescription) fail("alias_mismatch", "sector");
  if (cik !== issuerCik) fail("alias_mismatch", "cik");

  const plan = parsePlan(input.plan, "plan");
  const isPlanned = booleanValue(input.isPlanned, "isPlanned");
  if (isPlanned !== (plan.status === "planned")) fail("alias_mismatch", "isPlanned");

  const isAmendment = booleanValue(input.isAmendment, "isAmendment");
  const amendmentState = literalValue(
    input.amendmentState,
    ["original", "amendment"] as const,
    "amendmentState"
  ) as AmendmentState;
  if (isAmendment !== (amendmentState === "amendment")) {
    fail("alias_mismatch", "amendmentState");
  }

  const securityStatus = literalValue(
    input.securityStatus,
    ["common_equity", "non_common", "derivative", "unknown"] as const,
    "securityStatus"
  ) as SecurityStatus;
  const isCommonEquity = booleanValue(input.isCommonEquity, "isCommonEquity");
  const isDerivative = booleanValue(input.isDerivative, "isDerivative");
  if (isCommonEquity !== (securityStatus === "common_equity")) {
    fail("security_invariant", "isCommonEquity");
  }
  if (isDerivative !== (securityStatus === "derivative")) {
    fail("security_invariant", "isDerivative");
  }

  const signal = parseSignal(input.signal, "signal");
  return {
    transactionId,
    id,
    ticker: stringValue(input.ticker, "ticker", false),
    companyName: stringValue(input.companyName, "companyName", false),
    exchange: stringValue(input.exchange, "exchange", false),
    industryDescription,
    sector,
    issuerCik,
    cik,
    ownerCik: nullableString(input.ownerCik, "ownerCik"),
    ownerIdentityKey: nullableString(input.ownerIdentityKey, "ownerIdentityKey"),
    insiderName: stringValue(input.insiderName, "insiderName", false),
    relationshipFlags: arrayValue(input.relationshipFlags, "relationshipFlags", (item, path) =>
      literalValue(
        item,
        ["CEO", "CFO", "Director", "Officer", "10% Owner", "Other"] as const,
        path
      ) as InsiderRole
    ),
    role: literalValue(
      input.role,
      ["CEO", "CFO", "Director", "Officer", "10% Owner", "Other"] as const,
      "role"
    ) as InsiderRole,
    relationship: stringValue(input.relationship, "relationship"),
    transactionCode: stringValue(input.transactionCode, "transactionCode"),
    tradeType: literalValue(
      input.tradeType,
      ["buy", "sell", "option", "award"] as const,
      "tradeType"
    ) as TradeType,
    acquiredDisposed: literalValue(
      input.acquiredDisposed,
      ["A", "D"] as const,
      "acquiredDisposed"
    ) as AcquiredDisposed,
    shares: finiteNumber(input.shares, "shares"),
    price: nullableNumber(input.price, "price"),
    value: nullableNumber(input.value, "value"),
    valueQuality: literalValue(
      input.valueQuality,
      ["reported", "derived", "missing", "suspect"] as const,
      "valueQuality"
    ) as ValueQuality,
    holdingsBefore: nullableNumber(input.holdingsBefore, "holdingsBefore"),
    holdingsAfter: nullableNumber(input.holdingsAfter, "holdingsAfter"),
    ownershipChangePct: nullableNumber(input.ownershipChangePct, "ownershipChangePct"),
    tradeDate: nullableDate(input.tradeDate, "tradeDate"),
    filingDate: dateValue(input.filingDate, "filingDate"),
    filingLagDays: nullableInteger(input.filingLagDays, "filingLagDays"),
    accessionNumber: stringValue(input.accessionNumber, "accessionNumber", false),
    secUrl: secArchiveUrlValue(input.secUrl, "secUrl"),
    securityTitle: stringValue(input.securityTitle, "securityTitle", false),
    securityStatus,
    isCommonEquity,
    directOwnership: booleanValue(input.directOwnership, "directOwnership"),
    isDerivative,
    plan,
    isPlanned,
    isAmendment,
    amendmentState,
    supersessionState: literalValue(
      input.supersessionState,
      ["active", "superseding", "superseded"] as const,
      "supersessionState"
    ) as SupersessionState,
    flags: stringArray(input.flags, "flags"),
    footnotes: stringArray(input.footnotes, "footnotes"),
    cluster: parseCluster(input.cluster, "cluster"),
    programContext: parseProgramContext(input.programContext, "programContext"),
    marketContext: parseMarketContext(input.marketContext, "marketContext"),
    signal
  };
}

function sameCounters<T extends object>(left: T, right: T): boolean {
  return (Object.keys(left) as Array<keyof T>).every((key) => left[key] === right[key]);
}

export function validateLoadedDataset(
  manifest: DashboardManifest,
  rows: CanonicalTransaction[]
): void {
  if (manifest.transactionCount !== rows.length) {
    fail("transaction_count_mismatch", "transactionCount");
  }
  if (manifest.exportCounters.transactions !== rows.length) {
    fail("transaction_count_mismatch", "exportCounters.transactions");
  }
  if (manifest.counters.export.transactions !== rows.length) {
    fail("transaction_count_mismatch", "counters.export.transactions");
  }
  if (manifest.exportCounters.shards !== manifest.transactionShardUrls.length) {
    fail("shard_count_mismatch", "exportCounters.shards");
  }
  if (manifest.counters.export.shards !== manifest.transactionShardUrls.length) {
    fail("shard_count_mismatch", "counters.export.shards");
  }
  if (!sameCounters(manifest.downloadCounters, manifest.counters.download)) {
    fail("counter_mismatch", "counters.download");
  }
  if (!sameCounters(manifest.parseCounters, manifest.counters.parse)) {
    fail("counter_mismatch", "counters.parse");
  }
  if (!sameCounters(manifest.exportCounters, manifest.counters.export)) {
    fail("counter_mismatch", "counters.export");
  }
  if (!sameCounters(manifest.validationCounters, manifest.counters.validation)) {
    fail("counter_mismatch", "counters.validation");
  }

  const ids = new Set<string>();
  rows.forEach((row, index) => {
    const id = row.transactionId;
    if (typeof id !== "string" || id.trim() === "") {
      fail("empty_transaction_id", `rows[${index}].transactionId`);
    }
    if (ids.has(id)) fail("duplicate_transaction_id", `rows[${index}].transactionId`);
    ids.add(id);
    if (row.id !== id) fail("id_alias_mismatch", `rows[${index}].id`);
    if (row.cik !== row.issuerCik) fail("alias_mismatch", `rows[${index}].cik`);
    if (row.sector !== row.industryDescription) {
      fail("alias_mismatch", `rows[${index}].sector`);
    }
    if (row.signal.version !== manifest.signalVersion) {
      fail("signal_version_mismatch", `rows[${index}].signal.version`);
    }
  });

  rows.forEach((row, rowIndex) => {
    row.cluster?.memberTransactionIds.forEach((memberId, memberIndex) => {
      if (!ids.has(memberId)) {
        fail(
          "cluster_member_missing",
          `rows[${rowIndex}].cluster.memberTransactionIds[${memberIndex}]`
        );
      }
    });
  });
}

export function computeFreshness(
  manifest: Pick<DashboardManifest, "ingestState" | "staleAfter">,
  now: Date
): "fresh" | "stale" {
  const nowTime = now.getTime();
  if (!Number.isFinite(nowTime)) fail("invalid_timestamp", "now");
  return manifest.ingestState === "completed" && nowTime <= Date.parse(manifest.staleAfter)
    ? "fresh"
    : "stale";
}
